import { useState } from 'react';

import { Icon } from '@dcc-erp/ui';

import { useUpdateBranch, useUpdateSupplier } from '../../lib/queries/suppliers';
import { AppModal } from '../AppModal';

import type { SupplierWithBranches } from '../../lib/queries/suppliers';

// RECONCILED with the real Plan A codebase (real file wins):
//  * `SupplierWithBranches` is NOT a `@dcc-erp/shared` export (the plan named one
//    that does not exist) — it's the local entity+branches shape declared in
//    lib/queries/suppliers, whose branch rows live under `.vendors` (= Vendor
//    rows), NOT `.branches`.
//  * The branch update body is `UpdateSupplierBranch` (a Vendor row), NOT the
//    plan's `UpdateBranch` (the company-location master, a different shape).
//  * Label class is `meta` — the plan's `fld-label` class does not exist in the
//    codebase (same fix as B1.3/B1.4).
const labelClass = 'meta';
const labelStyle = { display: 'block', marginBottom: 6 } as const;

export interface EditBranchModalProps {
  companyId: string;
  supplier: SupplierWithBranches;
  // vendorId = edit that branch; null = edit the entity-level defaults.
  vendorId: string | null;
  onClose: () => void;
}

export function EditBranchModal({ companyId, supplier, vendorId, onClose }: EditBranchModalProps) {
  const updateSupplier = useUpdateSupplier(companyId);
  const updateBranch = useUpdateBranch(companyId);
  const branch = vendorId ? (supplier.vendors.find((b) => b.id === vendorId) ?? null) : null;
  const isEntity = vendorId === null;

  const [terms, setTerms] = useState(supplier.terms ?? '');
  const [branchName, setBranchName] = useState(branch?.branchName ?? '');
  const [addressTh, setAddressTh] = useState(branch?.addressTh ?? '');
  const [isActive, setIsActive] = useState<boolean>(
    isEntity ? supplier.isActive : (branch?.isActive ?? true),
  );

  async function handleSubmit() {
    if (isEntity) {
      // Hook params already type `data` as UpdateSupplier / UpdateSupplierBranch
      // (both fully partial), so no cast is needed — keeps eslint happy.
      await updateSupplier.mutateAsync({ id: supplier.id, data: { terms, isActive } });
    } else if (vendorId) {
      await updateBranch.mutateAsync({
        id: supplier.id,
        vendorId,
        data: {
          ...(branchName ? { branchName } : {}),
          ...(addressTh ? { addressTh } : {}),
          isActive,
        },
      });
    }
    onClose();
  }

  const busy = updateSupplier.isPending || updateBranch.isPending;

  return (
    <AppModal
      title={isEntity ? 'แก้ไขผู้ขาย' : 'แก้ไขสาขา'}
      subtitle={supplier.nameTh}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={busy}
            onClick={() => void handleSubmit()}
          >
            <Icon name="check" size={14} /> {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </>
      }
    >
      {isEntity ? (
        <div>
          <label className={labelClass} style={labelStyle}>
            เงื่อนไขชำระ (terms)
          </label>
          <input
            className="input"
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <label className={labelClass} style={labelStyle}>
              ชื่อสาขา
            </label>
            <input
              className="input"
              value={branchName}
              onChange={(e) => setBranchName(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>
              ที่อยู่
            </label>
            <input
              className="input"
              value={addressTh}
              onChange={(e) => setAddressTh(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      )}
      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 14,
          cursor: 'pointer',
        }}
      >
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        ใช้งาน (active)
      </label>
      {(updateSupplier.error ?? updateBranch.error) && (
        <div style={{ color: 'var(--negative)', marginTop: 12 }}>
          บันทึกไม่สำเร็จ: {(updateSupplier.error ?? updateBranch.error)?.message}
        </div>
      )}
    </AppModal>
  );
}
